import { redirect } from "@tanstack/react-router";
import Cookies from "js-cookie";

import { refreshToken } from "@/lib/utils";
import { useDefaultStore } from "@/stores";

function isAccessTokenValidForNow(): boolean {
	const { token, decodedAccessToken } = useDefaultStore.getState();

	if (!token) return false;
	// 디코딩 실패 등으로 exp를 모르면 여기서 refresh를 강제하지 않고, 실제 API 401에서 처리한다.
	if (!decodedAccessToken?.exp) return true;

	return decodedAccessToken.exp * 1000 >= Date.now() + 30000;
}

/**
 * 토큰이 만료되었는지 확인하고 만료되었으면 토큰 갱신
 * @returns 토큰이 정상적으로 갱신되었는지 여부
 */
export async function authMiddleware(): Promise<boolean> {
	const refresh_token = Cookies.get("library-refresh-token");

	if (!refresh_token) {
		return false;
	}

	// 이미 유효한 access token이 있으면 네트워크 refresh는 하지 않는다.
	if (isAccessTokenValidForNow()) {
		return true;
	}

	try {
		await refreshToken();
		return true;
	} catch {
		return false;
	}
}

/**
 * 인증이 필요한 라우트에서 사용하는 함수
 * 토큰이 없거나 유효하지 않으면 로그인 페이지로 리다이렉트
 * @param callbackPath 리다이렉트 후 돌아올 경로 (기본값: 현재 경로)
 */
export async function requireAuth(callbackPath?: string): Promise<void> {
	const token = Cookies.get("library-refresh-token");

	if (!token) {
		throw redirect({
			to: "/login",
			search: { callback: callbackPath || window.location.pathname },
		});
	}

	// 이미 유효한 access token이 있으면 refresh하지 않는다. (라우트 preload/hover로 beforeLoad가 자주 실행될 수 있음)
	if (isAccessTokenValidForNow()) {
		return;
	}

	// refresh token은 있지만 access token이 없거나 만료 임박이면 갱신 시도
	try {
		await refreshToken();
	} catch {
		// 토큰 갱신 실패 시 로그인 페이지로 리다이렉트
		throw redirect({
			to: "/login",
			search: { callback: callbackPath || window.location.pathname },
		});
	}
}

/**
 * 관리자 권한이 필요한 라우트에서 사용하는 함수
 * 인증을 먼저 확인하고, user_level이 2 미만이면 홈으로 리다이렉트
 */
export async function requireAdmin(callbackPath?: string): Promise<void> {
	await requireAuth(callbackPath);

	const { decodedAccessToken } = useDefaultStore.getState();
	if (!decodedAccessToken || decodedAccessToken.user_level < 2) {
		throw redirect({ to: "/" });
	}
}
